import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChatCircleDots, Robot, X, Minus } from '@phosphor-icons/react';
import AiAssistantChat from './AiAssistantChat';

const STORAGE_KEY = 'easyitsm-ai-widget-pos';

function loadPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.bottom === 'number' && typeof parsed.right === 'number') {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function savePosition(pos) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    /* ignore */
  }
}

function AiAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(() => loadPosition() || { bottom: 24, right: 24 });
  const dragRef = useRef(null);
  const dragging = useRef(false);
  const moved = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, bottom: 24, right: 24 });

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragging.current = true;
    moved.current = false;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      bottom: position.bottom,
      right: position.right,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [position.bottom, position.right]);

  const onPointerMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved.current = true;
    const next = {
      bottom: Math.max(16, Math.min(window.innerHeight - 80, dragStart.current.bottom - dy)),
      right: Math.max(16, Math.min(window.innerWidth - 80, dragStart.current.right - dx)),
    };
    setPosition(next);
  }, []);

  const onPointerUp = useCallback((e) => {
    if (!dragging.current) return;
    dragging.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (moved.current) {
      setPosition((pos) => {
        savePosition(pos);
        return pos;
      });
    }
  }, []);

  const toggleOpen = useCallback(() => {
    if (moved.current) {
      moved.current = false;
      return;
    }
    setOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div
      ref={dragRef}
      className="ai-assistant-widget"
      style={{ bottom: position.bottom, right: position.right }}
    >
      {open && (
        <div className="ai-assistant-panel" role="dialog" aria-label="AI Assistant">
          <div className="ai-assistant-panel-header">
            <div className="ai-assistant-panel-title">
              <Robot size={20} weight="duotone" color="#002FA7" />
              <div>
                <span className="ai-assistant-panel-name">AI Assistant</span>
                <span className="ai-assistant-panel-sub">EasyITSM copilot</span>
              </div>
            </div>
            <div className="ai-assistant-panel-actions">
              <button
                type="button"
                className="ai-assistant-panel-btn"
                onClick={() => setOpen(false)}
                title="Minimize"
                aria-label="Minimize chat"
              >
                <Minus size={16} weight="bold" />
              </button>
              <button
                type="button"
                className="ai-assistant-panel-btn"
                onClick={() => setOpen(false)}
                title="Close"
                aria-label="Close chat"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          </div>
          <div className="ai-assistant-panel-body">
            <AiAssistantChat variant="widget" />
          </div>
        </div>
      )}

      <button
        type="button"
        className={`ai-assistant-fab${open ? ' ai-assistant-fab-open' : ''}`}
        onClick={toggleOpen}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        aria-label={open ? 'Close AI Assistant' : 'Open AI Assistant'}
        aria-expanded={open}
        data-testid="ai-assistant-fab"
      >
        {open ? (
          <X size={26} weight="bold" />
        ) : (
          <ChatCircleDots size={28} weight="fill" />
        )}
      </button>
    </div>
  );
}

export default AiAssistantWidget;
